import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  collectionGroup,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { firestore } from './firebase/firebase';
import { functions } from './firebase/firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Constantes pour les statuts de tickets
 */
export const TICKET_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
  CLOSED: 'closed'
};

/**
 * Constantes pour les priorités de tickets
 */
export const TICKET_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Constantes pour les catégories de tickets
 */
export const TICKET_CATEGORY = {
  BUG: 'bug',
  FEATURE_REQUEST: 'feature_request',
  ACCOUNT: 'account',
  BILLING: 'billing',
  OTHER: 'other'
};

/**
 * Récupère tous les tickets de support à travers tous les utilisateurs
 * @param {Object} options - Options de filtrage et pagination
 * @param {Array|string} options.status - Statut(s) des tickets à filtrer
 * @param {string} options.priority - Priorité des tickets à filtrer
 * @param {string} options.searchTerm - Terme de recherche
 * @param {number} options.limit - Nombre maximum de tickets à récupérer
 * @param {Object} options.lastVisible - Dernier document visible pour la pagination
 * @returns {Promise<{tickets: Array, lastVisible: Object}>} Liste des tickets et dernier document visible
 */
export const getAllTickets = async (options = {}) => {
  try {
    console.log('Récupération de tous les tickets avec options:', options);
    
    // Utiliser collectionGroup pour récupérer tous les tickets dans tous les sous-dossiers "support_tickets"
    const ticketsRef = collectionGroup(firestore, 'support_tickets');
    
    // Construire la requête de base
    let ticketsQuery = query(ticketsRef, orderBy('updatedAt', 'desc'));
    
    // Ajouter des filtres si spécifiés
    if (options.status) {
      if (Array.isArray(options.status)) {
        ticketsQuery = query(ticketsQuery, where('status', 'in', options.status));
      } else {
        ticketsQuery = query(ticketsQuery, where('status', '==', options.status));
      }
    }
    
    if (options.priority) {
      ticketsQuery = query(ticketsQuery, where('priority', '==', options.priority));
    }
    
    if (options.category) {
      ticketsQuery = query(ticketsQuery, where('category', '==', options.category));
    }
    
    // Pour les filtres de date, nous allons devoir filtrer après la requête
    // car Firestore ne permet pas de multiples conditions de filtre sur des champs différents
    // sans index composites
    
    // Ajouter la pagination
    if (options.lastVisible) {
      ticketsQuery = query(ticketsQuery, startAfter(options.lastVisible));
    }
    
    // Limiter les résultats
    const limitSize = options.limit || 20;
    ticketsQuery = query(ticketsQuery, limit(limitSize));
    
    // Exécuter la requête
    const snapshot = await getDocs(ticketsQuery);
    
    // Traiter les résultats
    let tickets = await Promise.all(snapshot.docs.map(async (ticketDoc) => {
      // Obtenir les données du ticket
      const ticketData = ticketDoc.data();
      
      // Extraire le userId depuis le chemin de référence
      const path = ticketDoc.ref.path;
      const pathParts = path.split('/');
      const userId = pathParts[1]; // Le format est "users/{userId}/support_tickets/{ticketId}"
      
      // Récupérer les informations de l'utilisateur
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      // Formater les timestamps
      const formattedTicket = {
        id: ticketDoc.id,
        userId: userId,
        userInfo: {
          email: userData?.email || 'Email non disponible',
          displayName: userData?.firstName && userData?.lastName 
            ? `${userData.firstName} ${userData.lastName}` 
            : userData?.email || 'Utilisateur inconnu'
        },
        ...ticketData,
        // Convertir les timestamps en dates JavaScript
        createdAt: ticketData.createdAt?.toDate(),
        updatedAt: ticketData.updatedAt?.toDate(),
        // Traiter les messages
        messages: ticketData.messages?.map(msg => ({
          ...msg,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp : msg.timestamp?.toDate()
        })) || []
      };
      
      return formattedTicket;
    }));
    
    // Filtrage côté client pour les dates
    if ((options.dateFrom || options.dateTo) && tickets.length > 0) {
      const dateFrom = options.dateFrom ? new Date(options.dateFrom) : null;
      const dateTo = options.dateTo ? new Date(options.dateTo) : null;
      
      if (dateFrom) {
        dateFrom.setHours(0, 0, 0, 0); // Début de journée
      }
      
      if (dateTo) {
        dateTo.setHours(23, 59, 59, 999); // Fin de journée
      }
      
      tickets = tickets.filter(ticket => {
        const updatedAt = ticket.updatedAt || ticket.createdAt;
        if (!updatedAt) return true;
        
        if (dateFrom && dateTo) {
          return updatedAt >= dateFrom && updatedAt <= dateTo;
        } else if (dateFrom) {
          return updatedAt >= dateFrom;
        } else if (dateTo) {
          return updatedAt <= dateTo;
        }
        
        return true;
      });
    }
    
    console.log(`${tickets.length} tickets récupérés`);
    
    return {
      tickets,
      lastVisible: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des tickets:', error);
    throw error;
  }
};

/**
 * Récupère les détails d'un ticket spécifique
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket
 * @returns {Promise<Object>} Détails du ticket
 */
export const getTicketDetails = async (userId, ticketId) => {
  try {
    const ticketRef = doc(firestore, 'users', userId, 'support_tickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);
    
    if (!ticketDoc.exists()) {
      throw new Error(`Ticket ${ticketId} non trouvé`);
    }
    
    // Récupérer les informations de l'utilisateur
    const userDoc = await getDoc(doc(firestore, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    const ticketData = ticketDoc.data();
    
    // Formater les timestamps
    return {
      id: ticketDoc.id,
      userId: userId,
      userInfo: {
        email: userData?.email || 'Email non disponible',
        displayName: userData?.firstName && userData?.lastName 
          ? `${userData.firstName} ${userData.lastName}` 
          : userData?.email || 'Utilisateur inconnu'
      },
      ...ticketData,
      // Convertir les timestamps en dates JavaScript
      createdAt: ticketData.createdAt?.toDate(),
      updatedAt: ticketData.updatedAt?.toDate(),
      // Traiter les messages
      messages: ticketData.messages?.map(msg => ({
        ...msg,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp : msg.timestamp?.toDate()
      })) || []
    };
  } catch (error) {
    console.error(`Erreur lors de la récupération du ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Ajoute un message à un ticket de support
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket
 * @param {string} content - Contenu du message
 * @param {string} staffName - Nom de l'administrateur qui répond
 * @returns {Promise<boolean>} Statut de la mise à jour
 */
export const addMessageToTicket = async (userId, ticketId, content, staffName = 'Support MSD') => {
  try {
    const ticketRef = doc(firestore, 'users', userId, 'support_tickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);
    
    if (!ticketDoc.exists()) {
      throw new Error(`Ticket ${ticketId} non trouvé`);
    }
    
    const ticketData = ticketDoc.data();
    const existingMessages = ticketData.messages || [];
    
    // Créer le nouveau message
    const newMessage = {
      content: content,
      timestamp: new Date(),  // Utiliser new Date() car nous sommes dans un tableau
      isStaff: true,
      authorId: 'admin',
      authorName: staffName
    };
    
    // Mettre à jour le ticket
    await updateDoc(ticketRef, {
      messages: [...existingMessages, newMessage],
      updatedAt: serverTimestamp(),
      // Si le ticket est à l'état 'new', le passer à 'in_progress'
      ...(ticketData.status === TICKET_STATUS.NEW ? { status: TICKET_STATUS.IN_PROGRESS } : {})
    });
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de l'ajout du message au ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Met à jour le statut d'un ticket
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket
 * @param {string} status - Nouveau statut du ticket
 * @returns {Promise<boolean>} Statut de la mise à jour
 */
export const updateTicketStatus = async (userId, ticketId, status) => {
  try {
    if (!Object.values(TICKET_STATUS).includes(status)) {
      throw new Error(`Statut invalide: ${status}`);
    }
    
    const ticketRef = doc(firestore, 'users', userId, 'support_tickets', ticketId);
    
    await updateDoc(ticketRef, {
      status: status,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du statut du ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Compte les tickets par statut
 * @returns {Promise<Object>} Nombre de tickets par statut
 */
export const getTicketsCountByStatus = async () => {
  try {
    const ticketsRef = collectionGroup(firestore, 'support_tickets');
    const snapshot = await getDocs(ticketsRef);
    
    const counts = {
      all: snapshot.size,
      [TICKET_STATUS.NEW]: 0,
      [TICKET_STATUS.IN_PROGRESS]: 0,
      [TICKET_STATUS.RESOLVED]: 0,
      [TICKET_STATUS.CLOSED]: 0
    };
    
    snapshot.forEach(doc => {
      const status = doc.data().status;
      if (status && counts[status] !== undefined) {
        counts[status]++;
      }
    });
    
    return counts;
  } catch (error) {
    console.error('Erreur lors du comptage des tickets:', error);
    throw error;
  }
};

/**
 * Ajoute une note interne à un ticket (visible uniquement par les administrateurs)
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket
 * @param {string} content - Contenu de la note
 * @param {string} adminName - Nom de l'administrateur qui ajoute la note
 * @returns {Promise<boolean>} Statut de la mise à jour
 */
export const addInternalNoteToTicket = async (userId, ticketId, content, adminName = 'Admin MSD') => {
  try {
    const ticketRef = doc(firestore, 'users', userId, 'support_tickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);
    
    if (!ticketDoc.exists()) {
      throw new Error(`Ticket ${ticketId} non trouvé`);
    }
    
    const ticketData = ticketDoc.data();
    const existingNotes = ticketData.internalNotes || [];
    
    // Créer la nouvelle note
    const newNote = {
      content: content,
      timestamp: new Date(),  // Utiliser new Date() car nous sommes dans un tableau
      adminName: adminName,
      adminId: 'admin' // Idéalement, utiliser l'ID réel de l'administrateur
    };
    
    // Mettre à jour le ticket
    await updateDoc(ticketRef, {
      internalNotes: [...existingNotes, newNote],
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error(`Erreur lors de l'ajout de la note interne au ticket ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Observe les nouveaux tickets en temps réel
 * @param {Function} callback - Fonction appelée lorsqu'un nouveau ticket est créé
 * @returns {Function} Fonction de désabonnement
 */
export const observeNewTickets = (callback) => {
  try {
    console.log('Démarrage de l\'observation des nouveaux tickets...');
    const ticketsRef = collectionGroup(firestore, 'support_tickets');
    
    // Observer uniquement les tickets avec le statut NEW créés dans les dernières 24h
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    const newTicketsQuery = query(
      ticketsRef,
      where('status', '==', TICKET_STATUS.NEW),
      where('createdAt', '>=', oneDayAgo),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    
    // S'abonner aux changements dans la collection
    const unsubscribe = onSnapshot(newTicketsQuery, {
      next: async (snapshot) => {
        console.log(`Notification snapshot reçu: ${snapshot.docChanges().length} changements`);
        
        // Détecter les tickets ajoutés récemment
        const addedTickets = snapshot.docChanges()
          .filter(change => change.type === 'added')
          .map(change => ({
            id: change.doc.id,
            ...change.doc.data(),
            // Extraire userId du chemin
            userId: change.doc.ref.path.split('/')[1]
          }));
        
        console.log(`Tickets ajoutés détectés: ${addedTickets.length}`);
        
        if (addedTickets.length > 0) {
          // Récupérer les infos utilisateur pour chaque ticket
          const enrichedTickets = await Promise.all(addedTickets.map(async ticket => {
            try {
              const userDoc = await getDoc(doc(firestore, 'users', ticket.userId));
              const userData = userDoc.exists() ? userDoc.data() : null;
              
              return {
                ...ticket,
                userInfo: {
                  email: userData?.email || 'Email non disponible',
                  displayName: userData?.firstName && userData?.lastName 
                    ? `${userData.firstName} ${userData.lastName}` 
                    : userData?.email || 'Utilisateur inconnu'
                },
                createdAt: ticket.createdAt?.toDate(),
                updatedAt: ticket.updatedAt?.toDate()
              };
            } catch (error) {
              console.error(`Erreur lors de la récupération des données utilisateur pour ${ticket.userId}:`, error);
              return ticket;
            }
          }));
          
          console.log('Appel du callback avec les tickets enrichis:', enrichedTickets);
          callback(enrichedTickets);
        }
      },
      error: (error) => {
        console.error('Erreur lors de l\'observation des nouveaux tickets:', error);
      }
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Erreur lors de la configuration de l\'observation des tickets:', error);
    return () => {}; // Fonction de désabonnement vide
  }
};

/**
 * Email « Vous avez reçu un nouveau message de notre équipe » (déclenché manuellement)
 * Réutilise la même onCall que les demandes de création : en passant ticketId,
 * l'extrait du dernier message est lu depuis le ticket de support au lieu du chat de demande.
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket de support
 */
export const sendStaffReplyNotification = async (userId, ticketId) => {
  const fn = httpsCallable(functions, 'sendStaffMessageEmail');
  const res = await fn({ userId, ticketId });
  return res.data;
};